import { FORBIDDEN_WRITE_TOOLS, MCP_TOOL_PREFIX, SAFE_ALLOW_TOOL_NAMES, TASK_AGENT_TOOL_NAMES } from "./constants.ts";

/** Shared by `mapToAgent` and `applyApprovals` so the §4.3 narrowing invariant can't drift between
 * the two call sites (spec Risks: "the invariant was aspirational before this spec"). */

export type ToolRule = { name: string; specifier: string | null };

const RULE_RE = /^([A-Za-z_][A-Za-z0-9_-]*)(?:\((.*)\))?$/;

export function parseToolRule(rule: string): ToolRule {
  const m = RULE_RE.exec(rule.trim());
  if (!m) return { name: rule.trim(), specifier: null };
  return { name: m[1]!, specifier: m[2] ?? null };
}

/** Matches `Edit`, `Write(…)`, `  multiedit (**)`, `NOTEBOOKEDIT`, etc: the tool name as its own
 * word (case-insensitive), followed by end-of-string or an optional-whitespace `(`. Deliberately
 * independent of `RULE_RE`'s stricter `Tool(specifier)` shape — a malformed rule like `"Edit (**)"`
 * (a space before the paren) should still be treated as forbidden even though `parseToolRule`
 * cannot parse a specifier out of it, because nothing guarantees the real Claude Code CLI is as
 * strict as our parser and rejects it outright rather than tolerating the extra space. */
const FORBIDDEN_WORD_RE = new RegExp(`^\\s*(${FORBIDDEN_WRITE_TOOLS.join("|")})\\s*(\\(|$)`, "i");

/** A single YAML array element can itself be a comma/semicolon-joined compound, e.g.
 * `tools: ["Bash(*), Edit(**)"]` — one array entry carrying two rules. `FORBIDDEN_WORD_RE` is
 * start-anchored so it would miss the `Edit(**)` half entirely if tested against the whole string.
 * Tokenizing first closes that gap: every comma/semicolon-separated piece is checked on its own. */
function ruleTokens(rule: string): string[] {
  return rule.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
}

/** True for `Edit`, `Write`, `MultiEdit`, `NotebookEdit`, any specifier, any case, any spacing
 * around the parenthesis, and any such rule hiding inside a comma/semicolon-joined compound string.
 * Never accepted from an import, approved or not (spec §4.3). */
export function isForbiddenWriteRule(rule: string): boolean {
  return ruleTokens(rule).some((tok) => FORBIDDEN_WORD_RE.test(tok));
}

export type RuleClassification =
  | { kind: "forbidden" }
  | { kind: "base" }
  | { kind: "safe" }
  | { kind: "mcp_auto"; server: string }
  | { kind: "unknown_subagent" }
  | { kind: "needs_approval" };

/** Classifies one candidate allow rule against Roomate's base allow list and the agent's own
 * resolved subagent names. Never classifies a forbidden write rule as anything but `"forbidden"`. */
export function classifyAllowRule(rule: string, base: readonly string[], knownSubagents: readonly string[]): RuleClassification {
  if (isForbiddenWriteRule(rule)) return { kind: "forbidden" };
  if (base.includes(rule)) return { kind: "base" };
  const { name, specifier } = parseToolRule(rule);
  const lower = name.toLowerCase();
  if ((SAFE_ALLOW_TOOL_NAMES as readonly string[]).includes(lower)) return { kind: "safe" };
  if ((TASK_AGENT_TOOL_NAMES as readonly string[]).includes(lower)) {
    if (specifier && knownSubagents.includes(specifier)) return { kind: "safe" };
    return { kind: "unknown_subagent" };
  }
  if (lower.startsWith(MCP_TOOL_PREFIX)) return { kind: "mcp_auto", server: name.slice(MCP_TOOL_PREFIX.length) };
  return { kind: "needs_approval" };
}

/** Final defense in depth: strips any forbidden write rule out of an allow list no matter how it
 * got there. Called once more right before `mapToAgent`/`applyApprovals` return their result, so a
 * bug in classification alone cannot widen a fence. */
export function sanitizeAllowList(rules: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rules) {
    if (isForbiddenWriteRule(r)) continue;
    if (seen.has(r)) continue;
    seen.add(r);
    out.push(r);
  }
  return out;
}
